import ApiModel from 'models/api-model';
import { isNullOrUndefined } from 'utils/object-utils';

export default class AutoTieoutMappedElements extends ApiModel({
  data: {
    targetElementsMap: {},
    sourceElementsMap: {},
    targetElementClicked: null,
    sourceElementClicked: null,
  },
}) {
  processResponse({ response }) {
    const targetElementsMap = {};
    const sourceElementsMap = {};

    const mappings = response?.data || [];

    mappings.forEach((mapping) => {
      const { targetElementId, sourceElementId, active } = mapping;

      if (
        !isNullOrUndefined(targetElementId) &&
        !isNullOrUndefined(sourceElementId) &&
        active
      ) {
        targetElementsMap[targetElementId] = mapping;
        sourceElementsMap[sourceElementId] = mapping;
      }
    });

    return { data: { targetElementsMap, sourceElementsMap } };
  }

  // get count of mapped elements
  getMappedElementsCount() {
    return Object.keys(this.data.targetElementsMap).length;
  }

  // Clear all data
  clear() {
    return this.merge({
      data: {
        targetElementsMap: {},
        sourceElementsMap: {},
        targetElementClicked: null,
        sourceElementClicked: null,
      },
    });
  }

  // Actions to set clicked elements
  setTargetElementClicked(payload) {
    return this.merge({
      data: {
        ...this.data,
        targetElementClicked: payload,
      },
    });
  }

  setSourceElementClicked(payload) {
    return this.merge({
      data: {
        ...this.data,
        sourceElementClicked: payload,
      },
    });
  }

  // Getters for clicked elements
  getTargetElementClicked() {
    return this.data.targetElementClicked;
  }

  getSourceElementClicked() {
    return this.data.sourceElementClicked;
  }

  // Getters for the maps
  getTargetElementsMap() {
    return this.data.targetElementsMap;
  }

  getSourceElementsMap() {
    return this.data.sourceElementsMap;
  }

  isElementExistsInTargetMap(targetElementId) {
    return targetElementId in this.data.targetElementsMap;
  }

  // Get source section ID for a given target element
  getSourceSectionIdForTargetElement(targetElementId) {
    const mapping = this.data.targetElementsMap[targetElementId];
    return mapping ? mapping.sourceSectionId : null;
  }

  // Get target section ID for a given source element
  getTargetSectionIdForSourceElement(sourceElementId) {
    const mapping = this.data.sourceElementsMap[sourceElementId];
    return mapping ? mapping.targetSectionId : null;
  }

  // Check if target element is active
  isTargetElementActive(targetElementId) {
    const mapping = this.data.targetElementsMap[targetElementId];
    return mapping ? Boolean(mapping.active) : false;
  }

  // Check if source element is active
  isSourceElementActive(sourceElementId) {
    const mapping = this.data.sourceElementsMap[sourceElementId];
    return mapping ? Boolean(mapping.active) : false;
  }

  // Helper methods for efficient lookups
  getTargetMapping(targetElementId) {
    return this.data.targetElementsMap[targetElementId];
  }

  getSourceMapping(sourceElementId) {
    return this.data.sourceElementsMap[sourceElementId];
  }

  // Get the corresponding source element for a target element
  getSourceElementForTarget(targetElementId) {
    const mapping = this.data.targetElementsMap[targetElementId];
    return mapping?.active ? mapping.sourceElementId : null;
  }

  // Get the corresponding target element for a source element
  getTargetElementForSource(sourceElementId) {
    const mapping = this.data.sourceElementsMap[sourceElementId];
    return mapping?.active ? mapping.targetElementId : null;
  }

  isMappedElementExistsInSourceMap(sourceElementId) {
    const mapping = this.data.sourceElementsMap[sourceElementId];
    return mapping?.active ? mapping.sourceElementId : null;
  }

  isMappedElementExistsInTargetMap(targetElementId) {
    const mapping = this.data.targetElementsMap[targetElementId];
    return mapping?.active ? mapping.targetElementId : null;
  }

  // Check if an element has a mapping
  hasTargetMapping(targetElementId) {
    return targetElementId in this.data.targetElementsMap;
  }

  hasSourceMapping(sourceElementId) {
    return sourceElementId in this.data.sourceElementsMap;
  }
}
